import * as React from 'react';
import { EnergyVectorsReadDTO, EnergyVectorsCreateDTO } from '../generated/api-client';
import { CrudConfig, CrudField, CrudDataGrid } from '../components/CrudDataGrid';
import { GridRowModel, GridRenderEditCellParams } from '@mui/x-data-grid';
import BoltIcon from '@mui/icons-material/Bolt';
import { Box, Typography } from '@mui/material';
import { PageLayout } from '../components/layout';
import { UnitOfMeasurementEditCell } from '../components/common';
import {
  AutocompleteEditCell,
  getOptionLabelFromValue,
} from '../components/common/AutocompleteEditCell';
import { formatUnitOfMeasurement } from '../utils/unitOfMeasurementFormatter';
import { apiClient } from '../services/apiClient';
import { useVectorTypeOptions } from '../hooks/useVectorTypeOptions';

export const EnergyVectorsPage: React.FC = () => {
  const { data: vectorTypeOptions = [] } = useVectorTypeOptions();

  const fields: CrudField[] = [
    {
      field: 'vector_type',
      headerName: 'Tipologia vettore',
      width: 200,
      editable: true,
      type: 'string',
      renderEditCell: (params: GridRenderEditCellParams) => (
        <AutocompleteEditCell
          {...params}
          options={vectorTypeOptions}
          placeholder="Tipologia vettore"
        />
      ),
      valueFormatter: (v) => getOptionLabelFromValue(vectorTypeOptions, v as string),
    },
    {
      field: 'unit_of_measurement',
      headerName: 'Unità di misura',
      width: 160,
      editable: true,
      type: 'string',
      renderEditCell: (params: GridRenderEditCellParams) => (
        <UnitOfMeasurementEditCell {...params} />
      ),
      valueFormatter: formatUnitOfMeasurement,
    },
    {
      field: 'co2_factor',
      headerName: 'Fattore kg CO₂',
      width: 140,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'tep_factor',
      headerName: 'Fattore tep',
      width: 140,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'tj_factor',
      headerName: 'Fattore TJ',
      width: 140,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'kwh_factor',
      headerName: 'Fattore kWh',
      width: 140,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'density',
      headerName: 'Densità (kg/l)',
      width: 140,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'is_default',
      headerName: 'Default',
      width: 100,
      editable: true,
      type: 'boolean',
      align: 'right',
      headerAlign: 'right',
    },
  ];

  const config: CrudConfig<EnergyVectorsReadDTO, EnergyVectorsCreateDTO> = {
    queryKey: 'energyVectors',
    fields,
    listFn: async () => {
      return await apiClient.list_items_energy_vectors__get();
    },
    createFn: async (data: EnergyVectorsCreateDTO) => {
      return await apiClient.create_item_energy_vectors__post(data);
    },
    updateFn: async (id: string, data: EnergyVectorsCreateDTO) => {
      return await apiClient.update_item_energy_vectors__item_id__put(id, data);
    },
    deleteFn: async (id: string) => {
      await apiClient.delete_item_energy_vectors__item_id__delete(id);
    },
    getId: (row: EnergyVectorsReadDTO) => row.id || '',
    getCreateDto: (row: GridRowModel) => {
      return new EnergyVectorsCreateDTO({
        table_code_index: row.table_code_index || '',
        vector_type: row.vector_type || '',
        unit_of_measurement: row.unit_of_measurement || undefined,
        co2_factor:
          row.co2_factor !== undefined && row.co2_factor !== null && row.co2_factor !== ''
            ? Number(row.co2_factor)
            : (undefined as any),
        tep_factor:
          row.tep_factor !== undefined && row.tep_factor !== null && row.tep_factor !== ''
            ? Number(row.tep_factor)
            : (undefined as any),
        tj_factor:
          row.tj_factor !== undefined && row.tj_factor !== null && row.tj_factor !== ''
            ? Number(row.tj_factor)
            : (undefined as any),
        kwh_factor:
          row.kwh_factor !== undefined && row.kwh_factor !== null && row.kwh_factor !== ''
            ? Number(row.kwh_factor)
            : (undefined as any),
        density:
          row.density !== undefined && row.density !== null && row.density !== ''
            ? Number(row.density)
            : (undefined as any),
        code: row.code || undefined,
        diagnosis_field_option_option_id:
          row.diagnosis_field_option_option_id !== undefined &&
          row.diagnosis_field_option_option_id !== null &&
          row.diagnosis_field_option_option_id !== ''
            ? Number(row.diagnosis_field_option_option_id)
            : 0,
        is_default: row.is_default ?? false,
      });
    },
  };

  return (
    <PageLayout
      header={
        <>
          <BoltIcon fontSize="large" />
          <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
            Fattori di conversione
          </Typography>
        </>
      }
    >
      <Box sx={{ p: 2 }}>
        <CrudDataGrid key="g1" config={config} />
      </Box>
    </PageLayout>
  );
};
